// frontend/src/components/billing/PricingGate.jsx
//
// The SaaS paywall: the popup (`EntryDialog`) a store whose access is denied
// meets instead of the app. Its one job is getting the merchant out of that
// state — band, both cadences and Subscribe; Contact sales for a band with no
// price; one "pricing not ready yet" popup for a store that cannot be priced
// yet, with Try again and Contact support; a refused Subscribe offers Contact
// support too. Both open the in-app support form, whose endpoint serves a
// denied store (support/views.py). The import the price waits on is not this
// gate's to show: the entry order puts the price-wait screen in front of it while
// that import runs or after it failed (`components/entry/entryScreen.js`
// PRICE_IMPORT), so this popup is reached for a store whose orders are in but
// whose revenue cannot be read (no store currency, no FX rate), and for the
// moment between a pricing read that started the import and the sync
// re-read it asks for (`onImportStarted`).
//
// Billing STATUS is the billing part of the entry read (`hooks/useEntryStatus`),
// which ProtectedLayout (App.jsx) reads once per store and passes down as
// `billingStatus`; this gate only fetches the pricing it renders.
// `billingStatus === null` means that read failed and the layout failed closed:
// the gate then shows only the retry popup, and `onRetry` re-runs the entry read
// (which swaps this gate for the loading screen and remounts it with a fresh
// payload).
//
// The free period gets ONE sentence here, for `trial_expired` and for a
// development store's `test_subscription` (`gateCopy`): no date, no day
// count. A development store past its evaluation sees the same ladder as
// anyone; the subscribe push is what refuses it (`dev_store_evaluation_closed`,
// rendered through SUBSCRIBE_ERROR_KEYS), because the status payload never
// asks Shopify who the store is and the click does.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import { apiRequest } from '../../services/SessionApiService';
import { apiErrorFrom } from '../../utils/parseApiError';
import { isPageBeingReplaced } from '../../utils/pageTeardown';
import ContactSupportButton from '../common/ContactSupportButton';
import EntryDialog from '../entry/EntryDialog';
import EntryLoading from '../entry/EntryLoading';
import { hasUsablePrices } from './pricingPayload';
import { gateCopy } from './gateCopy';
import { PlanHelp, PlanOffer } from './PlanCard';
import { PRICING_NOT_READY_STATUS, SUBSCRIBE_ERROR_KEYS } from './planCopy';
import ContactSalesButton from './ContactSalesButton';
import './PricingGate.css';

// "Contact support <tail>". A refusal a retry might clear ends in "if this
// continues"; a closed evaluation is final, so its tail names the one thing
// support can do about it.
const SUPPORT_TAIL_KEYS = {
  'billing.errors.devStoreEvaluationClosed': 'billing.errors.devStoreEvaluationClosedSupport',
};

/** @param {{errorKey: string|null}} props  the i18n key of the refusal */
function SubscribeError({ errorKey }) {
  const { t } = useTranslation();
  if (!errorKey) return null;
  return (
    <div className="pricing-gate-error">
      {t(errorKey)}
      {' '}
      <ContactSupportButton />
      {' '}{t(SUPPORT_TAIL_KEYS[errorKey] ?? 'billing.ifThisContinues')}
    </div>
  );
}

/** The gate when the price could not be read: one sentence and Try again. */
function RetryGate({ onRetry }) {
  const { t } = useTranslation();
  return (
    <EntryDialog
      title={t('billing.loadFailedTitle')}
      actions={<Button variant="contained" onClick={onRetry}>{t('billing.retry')}</Button>}
    >
      <Typography color="text.secondary">{t('billing.loadFailedBody')}</Typography>
      <PlanHelp />
    </EntryDialog>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.billingStatus  the store's billing status, or
 *   null when its read failed
 * @param {() => void} props.onRetry  re-run the billing check from the top
 * @param {() => void} props.onImportStarted  the pricing read answered "not
 *   ready" and started the import the price waits on: re-read the sync status,
 *   so the entry order swaps this card for the price-wait screen
 *   (`components/entry/entryScreen.js` PRICE_IMPORT)
 */
export default function PricingGate({ billingStatus, onRetry, onImportStarted }) {
  const { t } = useTranslation();
  const [pricing, setPricing] = useState(null);
  // null while loading or loaded; once the read ended without a priceable
  // payload: 'failed' (a read that answered nothing usable) or 'not_ready'
  // (the store cannot be priced yet).
  const [readFailure, setReadFailure] = useState(null);
  // Monthly by default: the toggle's opening position is the amount a
  // merchant approves if they never touch it.
  const [isAnnual, setIsAnnual] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    // No status payload — the layout's status fetch failed and it failed
    // closed. Nothing to load; the retry card below re-runs the check.
    if (!billingStatus) return undefined;
    let cancelled = false;
    async function loadPricing() {
      try {
        const res = await apiRequest('/api/billing/pricing/');
        if (res.status === PRICING_NOT_READY_STATUS) {
          if (cancelled) return;
          setReadFailure('not_ready');
          // The read started the import (or found one on its way): the sync
          // status decides whether the price-wait screen replaces this card.
          onImportStarted();
          return;
        }
        if (!res.ok) throw new Error(`Failed to load pricing: HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPricing(data);
      } catch (err) {
        // A request the browser killed because the user is leaving reached no
        // verdict, so it records none — see utils/pageTeardown. The re-arm
        // after a bfcache restore is the layout's (a fresh `billingStatus`
        // object re-runs this effect).
        if (isPageBeingReplaced()) return;
        console.error('PricingGate fetch failed:', err.message);
        if (!cancelled) setReadFailure('failed');
      }
    }
    loadPricing();
    return () => { cancelled = true; };
  }, [billingStatus, onImportStarted]);

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    setErrorKey(null);
    try {
      const response = await apiRequest('/api/billing/subscribe/', {
        method: 'POST',
        body: JSON.stringify({
          billing_interval: isAnnual ? 'annual' : 'monthly',
          return_to: 'app',
        }),
      });
      if (!response.ok) {
        const err = await apiErrorFrom(response, 'POST /api/billing/subscribe/');
        console.error('PricingGate subscribe refused:', err.message);
        const key = err.code && SUBSCRIBE_ERROR_KEYS[err.code];
        setErrorKey(key || 'billing.errors.subscribeFailed');
        setIsSubscribing(false);
        return;
      }
      const data = await response.json();
      if (data.confirmation_url) {
        window.location.assign(data.confirmation_url);
      } else {
        setErrorKey('billing.errors.noConfirmationUrl');
        setIsSubscribing(false);
      }
    } catch (err) {
      console.error('PricingGate subscribe failed:', err.message);
      setErrorKey('billing.errors.subscribeFailed');
      setIsSubscribing(false);
    }
  };

  // The status read failed: the merchant's state is unknown, so the only
  // action is re-running the check from the top.
  if (!billingStatus || readFailure === 'failed') {
    return <RetryGate onRetry={onRetry} />;
  }

  // The store cannot be priced (the pricing read answered 503). Try again, or
  // write to support — a merchant whose revenue cannot be read has no other
  // way forward, and the window does not reopen while they wait.
  if (readFailure === 'not_ready') {
    return (
      <EntryDialog
        title={t('billing.notReady.title')}
        actions={(
          <>
            <ContactSupportButton variant="outlined" />
            <Button variant="contained" onClick={onRetry}>{t('billing.retry')}</Button>
          </>
        )}
      >
        <Typography color="text.secondary">{t('billing.notReady.body')}</Typography>
        <PlanHelp />
      </EntryDialog>
    );
  }

  if (!pricing) {
    return <EntryLoading />;
  }

  // Not quoted yet — the Enterprise band with no claimed offer, the one
  // payload with null prices. A quoted store has real prices and subscribes
  // like any other plan.
  if (pricing.prices.contact_sales) {
    return (
      <EntryDialog
        title={t('billing.enterpriseTitle')}
        actions={<ContactSalesButton salesEmail={pricing.prices.sales_email} />}
      >
        <Typography color="text.secondary">{t('billing.enterpriseBody')}</Typography>
        <PlanHelp />
      </EntryDialog>
    );
  }

  // A payload with no numbers is broken, not a special case: never render a
  // "$0 per month" price or a live Subscribe button from missing data.
  if (!hasUsablePrices(pricing.prices)) {
    console.error('PricingGate: pricing payload carried no usable prices');
    return <RetryGate onRetry={onRetry} />;
  }

  // WHICH sentence is true is decided by `billing/paywallCopy`; `gateCopy`
  // maps its KIND onto this surface's keys.
  const { gateHeading, gateBanner, ctaLabel } = gateCopy(t, billingStatus);

  return (
    <EntryDialog
      title={gateHeading}
      actions={(
        <Button variant="contained" onClick={handleSubscribe} loading={isSubscribing} disabled={isSubscribing}>
          {ctaLabel}
        </Button>
      )}
    >
      <Typography color="text.secondary">{gateBanner}</Typography>
      <PlanOffer prices={pricing.prices} isAnnual={isAnnual} onIntervalChange={setIsAnnual} />
      <SubscribeError errorKey={errorKey} />
      <PlanHelp />
    </EntryDialog>
  );
}
